import {add, compose, head, path, pathOr} from 'ramda'
import {CollectionFactory, combineReducers} from '@modifi/redux'
import {getAuthenticatedAdminAresClient} from '@modifi/trade-api-clients'
import {ACTION_KEY, REDUCER_KEY} from './constants'
import {selectTransactionId} from '../../data/transaction'
import {prepareFilterParams} from '../filters/utils/getFilterObject'

const ACTION_SET_PAYMENT_SORT_PARAMS = 'payments/SET_SORT_PARAMS'
const ACTION_SET_PAYMENT_FILTER_PARAMS = 'payments/SET_FILTER_PARAMS'
const ACTION_SET_PAYMENT_PAGINATION_INDEX = 'payments/SET_PAGINATION_INDEX'
const ACTION_SET_PAYMENT_PAGINATION_PAGE_SIZE = 'payments/SET_PAGINATION_PAGE_SIZE'
const ACTION_RESET_PAYMENT_PARAMS = 'payments/RESET_PARAMS'

const customerMap = {
  BUYER: 'buyer',
  SELLER: 'seller',
}

const tradePartnerMap = {
  BUYER: 'seller',
  SELLER: 'buyer',
}
const initialFilterState = {
  sort: {
    orderBy: '',
    orderDirection: '',
  },
  filter: {},
  pagination: {
    pageIndex: 0,
    pageSize: 30,
  },
}

const filterReducer = (state = initialFilterState, {type, payload}) => {
  switch (type) {
    case ACTION_SET_PAYMENT_SORT_PARAMS: {
      return {
        ...state,
        sort: {
          orderBy: payload?.orderBy,
          orderDirection: payload?.orderDirection,
        },
      }
    }
    case ACTION_SET_PAYMENT_FILTER_PARAMS: {
      return {
        ...state,
        filter: {
          ...payload,
        },
      }
    }
    case ACTION_SET_PAYMENT_PAGINATION_INDEX: {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          pageIndex: payload,
        },
      }
    }
    case ACTION_SET_PAYMENT_PAGINATION_PAGE_SIZE: {
      return {
        ...state,
        pagination: {
          ...state.pagination,
          pageSize: payload,
          pageIndex: 0,
        },
      }
    }
    case ACTION_RESET_PAYMENT_PARAMS: {
      return initialFilterState
    }
    default:
      return state
  }
}

export const selectPaymentFilters = state => {
  const paymentFilters = pathOr({}, [REDUCER_KEY, 'filters', 'filter'], state)
  return {...paymentFilters}
}

export const selectPaymentSort = state => pathOr({}, [REDUCER_KEY, 'filters', 'sort'], state)

export const selectPaymentsCurrentPageIndex = state =>
  pathOr(0, [REDUCER_KEY, 'filters', 'pagination', 'pageIndex'], state)

export const selectPaymentsCurrentPageSize = state =>
  pathOr(0, [REDUCER_KEY, 'filters', 'pagination', 'pageSize'], state)

const {
  actions,
  selectors,
  reducer: collectionReducer,
} = CollectionFactory({
  actionNamePrefix: ACTION_KEY,
  reducerPath: [REDUCER_KEY, 'data'],
  fetchRequest: (state, params) => {
    const client = getAuthenticatedAdminAresClient()

    return client.Payments.open(params).then(res => {
      // adjustment to map customer & tradePartner
      // as buyer/seller depending on order.role
      const {payments, _meta} = res.data()
      const entities = payments.map(payment => {
        const {
          fundsTransfers,
          currency,
          paymentFees: {settlementCurrency},
          repayment,
        } = payment
        const firstFundsTransfer = head(fundsTransfers)
        const {fundsTransferDueDate, adjustedFundsTransferDueDate} = firstFundsTransfer
        const renderedFundsTransferDueDate =
          currency === settlementCurrency ? fundsTransferDueDate : adjustedFundsTransferDueDate
        return {
          ...payment,
          renderedFundsTransferDueDate,
          fundsTransfers: payment.fundsTransfers.map(fundsTransfer => ({
            ...fundsTransfer,
            openAmount:
              parseFloat(fundsTransfer.balance) === 0 ? repayment?.amountInSettlementCurrency : '0',
          })),
          order: {
            ...payment.order,
            customerCompanyDetails:
              payment.order[`${customerMap[payment.order.role]}CompanyDetails`],
            tradePartnerCompanyDetails:
              payment.order[`${tradePartnerMap[payment.order.role]}CompanyDetails`],
          },
        }
      })
      return {
        entities,
        meta: _meta || {},
      }
    })
  },
  entityPrimaryKeySelector: path(['id']),
})

export default combineReducers({
  data: collectionReducer,
  filters: filterReducer,
})

export const {selectByKeyWindow, selectIsKeyWindowLoading, selectMetaByKeyWindow} = selectors

export const {clear: aClearPayments} = actions

const createKeyWindowFromState = state => {
  const sort = selectPaymentSort(state)
  const filters = prepareFilterParams(selectPaymentFilters(state))
  const transactionId = selectTransactionId(state)
  const pageIndex = selectPaymentsCurrentPageIndex(state)
  const pageSize = selectPaymentsCurrentPageSize(state)
  const sortArray = {orderBy: [sort.orderBy], orderDirection: [sort.orderDirection]}
  return {
    transactionId,
    pageIndex,
    pageSize,
    ...sortArray,
    ...filters,
  }
}

export const selectCurrentPaymentsLoading = state =>
  selectIsKeyWindowLoading(state, createKeyWindowFromState(state))

export const selectCurrentPayments = state =>
  selectByKeyWindow(state, createKeyWindowFromState(state))

export const selectPaymentsTotalCount = state =>
  pathOr(0, ['totalCount'], selectMetaByKeyWindow(state, createKeyWindowFromState(state)))

export const selectPaymentsTotalPages = state =>
  compose(
    add(1),
    pathOr(0, ['maxPageIndex'])
  )(selectMetaByKeyWindow(state, createKeyWindowFromState(state)))

export const aResetSortAndFilters = () => ({
  type: ACTION_RESET_PAYMENT_PARAMS,
})

export const aSetSortParams = sort => ({
  type: ACTION_SET_PAYMENT_SORT_PARAMS,
  payload: sort,
})

export const aAddFilterParams = newFilter => (dispatch, getState) => {
  const existingFilters =
    newFilter['filter.externalId.repayment.buyerIntendedPurpose'] ||
    newFilter['filter.externalId.order.orderReferenceCode'] ||
    newFilter['filter.externalId.paymentReferenceCode']
      ? {} // reset other filters when filtering use above primary filters
      : selectPaymentFilters(getState())
  const updatedFilters = {
    ...existingFilters,
    ...newFilter,
  }

  Object.keys(updatedFilters).forEach(key => {
    if (!updatedFilters[key] || updatedFilters[key].length === 0) delete updatedFilters[key]
  })

  return dispatch({
    type: ACTION_SET_PAYMENT_FILTER_PARAMS,
    payload: updatedFilters,
  })
}

export const aSetFilterParams = newFilter => ({
  type: ACTION_SET_PAYMENT_FILTER_PARAMS,
  payload: newFilter,
})

export const aSetPaginationIndex = index => ({
  type: ACTION_SET_PAYMENT_PAGINATION_INDEX,
  payload: index,
})

export const aSetPaginationPageSize = size => ({
  type: ACTION_SET_PAYMENT_PAGINATION_PAGE_SIZE,
  payload: size,
})

export const aFetchPayments = () => (dispatch, getState) => {
  dispatch(actions.fetch(createKeyWindowFromState(getState())))
}

export const aFetchWithFilterParams = newFilter => dispatch => {
  dispatch(aAddFilterParams(newFilter))
}
